import { toast } from 'react-toastify';
import { toggleWalkthroughSyncAutomation } from 'src/services/client';
import { useCallback, useState } from 'react';

interface ConfirmModalProps {
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  variant: 'warning' | 'danger' | 'info' | 'confirmation';
  requireTypeToConfirm: boolean;
  confirmationWord: string;
}

interface UseWalkthroughSyncSuspendEnableReturn {
  isTogglingSuspend: boolean;
  isConfirmModalOpen: boolean;
  openSuspendConfirmModal: () => void;
  closeConfirmModal: () => void;
  confirmSuspendAction: () => Promise<void>;
  confirmModalProps: ConfirmModalProps;
}

const toastConfig = {
  autoClose: 5000,
  position: 'top-center' as const,
};

const useWalkthroughSyncSuspendEnable = (
  propertyId: string,
  syncAutomationSuspended: boolean,
  onToggleSuccess: (propertyId: string, newSuspendedState: boolean) => void
): UseWalkthroughSyncSuspendEnableReturn => {
  const [isTogglingSuspend, setIsTogglingSuspend] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const openSuspendConfirmModal = useCallback(() => {
    setIsConfirmModalOpen(true);
  }, []);

  const closeConfirmModal = useCallback(() => {
    setIsConfirmModalOpen(false);
  }, []);

  const confirmSuspendAction = useCallback(async () => {
    if (isTogglingSuspend || !propertyId) return;

    const newSuspendedState: boolean = !syncAutomationSuspended;
    setIsTogglingSuspend(true);

    try {
      const response: any = await toggleWalkthroughSyncAutomation(
        propertyId,
        newSuspendedState
      );

      if (response.ok || response.status === 200) {
        onToggleSuccess(propertyId, newSuspendedState);
        setIsConfirmModalOpen(false);
        toast.success(
          newSuspendedState
            ? 'Walkthrough sync automation disabled successfully.'
            : 'Walkthrough sync automation enabled successfully.',
          toastConfig
        );
      } else {
        toast.error(
          'Failed to update sync automation status. Please try again.',
          toastConfig
        );
      }
    } catch (error) {
      console.error('Error toggling walkthrough sync automation:', error);
      toast.error(
        'Failed to update sync automation status. Please try again.',
        toastConfig
      );
    } finally {
      setIsTogglingSuspend(false);
    }
  }, [propertyId, isTogglingSuspend, syncAutomationSuspended, onToggleSuccess]);

  const confirmModalProps: ConfirmModalProps = syncAutomationSuspended
    ? {
      title: 'Enable Walkthrough Sync Automation',
      message:
        'Are you sure you want to enable the walkthrough sync automation for this property? Units will be automatically toggled on or off to sync available walkthroughs to the mobile app based on the availability of a walkthrough in the "begin" status',
      confirmLabel: 'Enable Automation',
      cancelLabel: 'Cancel',
      variant: 'confirmation',
      requireTypeToConfirm: false,
      confirmationWord: '',
    }
    : {
      title: 'Disable Walkthrough Sync Automation',
      message:
        'Are you sure you want to disable the walkthrough sync automation for this property? Units will no longer be automatically toggled on or off to sync available walkthroughs to the mobile app. You will need to manage the walkthrough sync manually on a unit by unit basis.',
      confirmLabel: 'Disable Automation',
      cancelLabel: 'Cancel',
      variant: 'confirmation',
      requireTypeToConfirm: false,
      confirmationWord: '',
    };

  return {
    isTogglingSuspend,
    isConfirmModalOpen,
    openSuspendConfirmModal,
    closeConfirmModal,
    confirmSuspendAction,
    confirmModalProps,
  };
};

export default useWalkthroughSyncSuspendEnable;
